/* eslint-disable indent */
/* eslint-disable max-lines */
import { styled, safariAndFirefoxBold, onlySafariAndFirefox, th, css } from '@elliemae/ds-system';

import type { DSTabsInternalsT } from '../../sharedTypes.js';
import { DSTabsName, TABS_SLOTS, TAB_TYPES } from '../../constants/index.js';

interface StyledSelectionIndicatoPropsT {
  isDSMobile: boolean;
  tabType: DSTabsInternalsT.TabTypesT;
}

interface StyledSubTabsListPropsT {
  withCarousel: boolean;
}

interface StyledTabButtonPropsT {
  isActive: boolean;
  disabled: boolean;
  showSeparator: boolean;
  isDSMobile: boolean;
  fixedTabsHeaders: boolean;
  withCarousel: boolean;
  tabType: DSTabsInternalsT.TabTypesT;
}

interface StyledTabListPropsT {
  withCarousel: boolean;
  isDSMobile: boolean;
  fixedTabsHeaders: boolean;
  mobileGradients: DSTabsInternalsT.MobileGradientsT;
  tabType: DSTabsInternalsT.TabTypesT;
}

interface StyledMobileGradientPropsT {
  mobileGradients: DSTabsInternalsT.MobileGradientsT;
  left: number | undefined;
  width: number | undefined;
}

interface StyledTabWrapperPropsT {
  isDSMobile: boolean;
  fixedTabsHeaders: boolean;
}

export const StyledSelectionIndicator = styled.div<StyledSelectionIndicatoPropsT>`
  pointer-events: none;
  position: absolute;
  height: ${({ isDSMobile }) => (isDSMobile ? '2px' : '3px')};
  width: ${({ isDSMobile }) => (isDSMobile ? '100%' : 'calc(100% + 32px)')};
  left: ${({ isDSMobile }) => (isDSMobile ? '0' : '-16px')};
  top: ${({ tabType, isDSMobile }) => {
    if (isDSMobile) return `35px`;
    if (tabType === TAB_TYPES.NORMAL_SMALL) return `15px`;
    return `25px`;
  }};
  z-index: 1;
  margin-top: ${({ theme }) => theme.space.xxxs};
  pointer-events: none;
  transition: 200ms cubic-bezier(0.36, 0, 1, 1);

  ${({ isDSMobile }) =>
    isDSMobile
      ? ` 
  margin-left: 0;
  margin-top: 3px;
  
  `
      : ``}

  background: ${(props) => {
    const { theme, isDSMobile } = props;
    return !isDSMobile
      ? `linear-gradient(to right, transparent 0px, transparent 16px, ${th.color('brand-600')(
          props,
        )} 16px, ${th.color('brand-600')(props)} calc(100% - 16px), transparent calc(100% - 16px), transparent 100% )`
      : theme.colors.brand[600];
  }};
`;

export const StyledMobileGradient = styled.div.attrs(({ left = 0, width = 0 }: StyledMobileGradientPropsT) => ({
  style: { left: left - 10, maxWidth: width - left },
}))<StyledMobileGradientPropsT>`
  position: fixed;
  z-index: 15;
  top: 0;
  width: calc(100% + 20px);
  height: 40px;
  pointer-events: none;

  ${({ mobileGradients: { left, right } }) => {
    if (right && left)
      return `background: linear-gradient(90deg, #FFFFFF 10px, #FFFFFF00 34px, #FFFFFF00 calc(100% - 44px), #FFFFFF calc(100% - 20px));`;
    if (right)
      return `background: linear-gradient(90deg, #FFFFFF00 0%, #FFFFFF00 calc(100% - 44px), #FFFFFF calc(100% - 20px));`;
    if (left) return `background: linear-gradient(90deg, #FFFFFF 10px, #FFFFFF00 34px, #FFFFFF00 100%);`;
    return '';
  }};
`;

/**  CSS Setters */

export const StyledTabWrapper = styled.div<StyledTabWrapperPropsT>`
  display: flex;
  flex: 1;
  flex-wrap: nowrap;
  ${({ fixedTabsHeaders, isDSMobile }) => (!fixedTabsHeaders && isDSMobile ? '' : 'overflow-x: hidden;')}
  ${({ theme, isDSMobile }) => (isDSMobile ? `border-bottom: 1px solid ${theme.colors.neutral[200]};` : ``)}
`;

const baseButtonStyles = css<StyledTabButtonPropsT>`
  position: relative;
  background-color: transparent;
  border: none;
  cursor: pointer;
  transition: 100ms cubic-bezier(0, 0, 0.42, 1);
  text-transform: ${({ tabType, isDSMobile }) => {
    if (tabType === TAB_TYPES.SUBTABS) return 'capitalize';
    return isDSMobile ? 'none' : 'uppercase';
  }};
  letter-spacing: 0px;
  white-space: nowrap;
  padding: 2px 0;
  margin-right: ${({ fixedTabsHeaders, isDSMobile }) => {
    if (fixedTabsHeaders) return '0px';
    if (!fixedTabsHeaders && isDSMobile) return '14px';
    return '16px';
  }};

  margin-left: ${({ fixedTabsHeaders, isDSMobile }) => {
    if (fixedTabsHeaders) return '0px';
    if (!fixedTabsHeaders && isDSMobile) return '14px';
    return '16px';
  }};

  color: ${({ theme }) => theme.colors.neutral[700]};
  ${onlySafariAndFirefox('-webkit-text-stroke: 0.4px transparent;')}

  ${({ tabType }) => (tabType === TAB_TYPES.NORMAL ? `height: 32px;` : `height: 22px;`)}

  ${({ theme, isActive, disabled, isDSMobile, tabType }) =>
    isActive && !disabled
      ? `
          font-weight:  ${theme.fontWeights.semibold};
          ${safariAndFirefoxBold(`#353C46`)}

          &:hover {
            ${safariAndFirefoxBold(`#1E79C2`)}
          }

          &:after: {
            content: '';
            position: absolute;
            background: ${theme.colors.brand[600]};
            height: ${isDSMobile ? '2px' : '3px'};
            width: ${isDSMobile ? '100%' : 'calc(100% + 32px)'};
            left: ${isDSMobile ? '0' : '-16px'};
            bottom: ${(() => {
              if (isDSMobile) return '-2px';
              if (tabType === TAB_TYPES.NORMAL_SMALL) return '0';
              return '-10px';
            })()};
            z-index: 1;
          }
        `
      : `
      
      `}

  ${({ theme, disabled }) =>
    disabled
      ? `
          color: ${theme.colors.neutral[500]};
          cursor: not-allowed;
        `
      : `
      &:hover {
        color: ${theme.colors.brand[600]};
      }
          `}

  ${({ theme }) => `
    &[aria-disabled='true'] {
      color: ${theme.colors.neutral[500]};
      cursor: not-allowed;
    }
  `}

  ${({ theme, showSeparator, tabType }) =>
    showSeparator
      ? `
          &:not(:last-of-type):before
            {
              content: "";
              position: absolute;
              width: 1px;
              right: -16px;
              top: 50%;
              transform: translateY(-50%);
              height: ${tabType === TAB_TYPES.SUBTABS ? `10px` : `16px`};
              background: ${theme.colors.neutral[400]};
            }`
      : ``}

  ${({ theme, isDSMobile, tabType, isActive }) =>
    !isDSMobile
      ? `
  &:focus {
    outline: none;
    &:after {
      position: absolute;
      content: '';
      top: 0;
      left: -16px;
      width: calc(100% + 32px);
      height: 100%;
      z-index: 5;
      border-radius: 2px;
      border: 2px solid ${theme.colors.brand[700]};
    }

    ${
      tabType !== TAB_TYPES.SUBTABS && isActive
        ? `
      &:before {
        position: absolute;
        content: '';
        left: -16px;
        right: 0;
        top: calc(100% - 2px);
        transform: none;
        width: calc(100% + 32px);
        height: 2px;
        z-index: 10;
        background: linear-gradient(to right, transparent 0px, transparent 12px, white 12px, white 16px, transparent 16px,transparent calc(100% - 16px),  white calc(100% - 16px), white calc(100% - 12px),  transparent calc(100% - 12px), transparent 100%);
      }
    `
        : ``
    }
  }
  
  `
      : ``}

  
      
${({ theme, isActive, disabled, isDSMobile }) =>
    isDSMobile
      ? `
    height:  40px;
    color: ${disabled ? theme.colors.neutral[500] : theme.colors.neutral[700]};
    font-weight: ${theme.fontWeights.regular};
    padding: 0;
    font-size: 16px;
    line-height: 1.3;
    ${String(onlySafariAndFirefox('-webkit-text-stroke: 0.4px transparent;'))} 

    &:focus {
      font-weight: ${theme.fontWeights.regular};
      ${safariAndFirefoxBold('#006AA9')}
    }

    ${
      isActive
        ? `
        font-weight: ${theme.fontWeights.regular};
        ${safariAndFirefoxBold('#006AA9')}
        `
        : ``
    }

    ${
      isActive && !disabled
        ? `
        color:  ${theme.colors.brand[700]};
        line-height: 1.3;
        font-weight: ${theme.fontWeights.regular};
        -webkit-font-smoothing: subpixel-antialiased; 
        -webkit-text-stroke: 0.4px ${theme.colors.brand[700]};`
        : ``
    }

    ${
      !disabled
        ? `
        &:hover,
        &:focus {
          outline: none;
          color: ${theme.colors.brand[700]};
          ${safariAndFirefoxBold('#006AA9')}
        }`
        : ``
    }

    `
      : ``}
`;

export const StyledTabButton = styled('button', {
  name: DSTabsName,
  slot: TABS_SLOTS.TAB_BUTTON,
})<StyledTabButtonPropsT>`
  ${baseButtonStyles}
`;
export const StyledSubTabButton = styled('button', {
  name: DSTabsName,
  slot: TABS_SLOTS.SUBTAB_BUTTON,
})<StyledTabButtonPropsT>`
  ${baseButtonStyles}
`;

export const StyledSubTabsList = styled('div', {
  name: DSTabsName,
  slot: TABS_SLOTS.SUB_TAB_LIST,
})<StyledSubTabsListPropsT>`
  position: relative;
  display: flex;
  flex-wrap: nowrap;
  flex: 1;
  width: 100%;
  ${({ withCarousel }) => (withCarousel ? 'max-width: 100%;' : '')}
`;

export const StyledTabList = styled('div', { name: DSTabsName, slot: TABS_SLOTS.TAB_LIST })<StyledTabListPropsT>`
  position: relative;
  display: flex;
  flex-wrap: nowrap;
  flex: 1;
  width: 100%;
  z-index: 0;
  ${({ withCarousel, isDSMobile }) => (withCarousel && !isDSMobile ? `max-width: 100%;` : '')}

  ${({ theme, isDSMobile, tabType }) =>
    tabType !== TAB_TYPES.NORMAL_SMALL && !isDSMobile
      ? `
  &:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 1px;
    background:${theme.colors.neutral[400]};
    pointer-events: none;
    bottom: 1px;
  }
  `
      : ``}

  ${StyledTabButton} {
    padding-bottom: 0;
    padding-top: 0;
  }

  ${({ theme, isDSMobile }) =>
    isDSMobile
      ? `
        
         background: ${theme.colors.neutral['000']};`
      : ``}

  ${({ isDSMobile, fixedTabsHeaders }) =>
    isDSMobile
      ? `
      ${
        !fixedTabsHeaders
          ? `
          z-index: 0;
          overflow-x: auto;
          overflow-y: hidden;
          &::-webkit-scrollbar {
            display: none;
          }`
          : `
         display: flex;
        ${StyledTabButton} {
          flex: 1;
          overflow-x: hidden;
        }`
      }`
      : ``}
`;

export const StyledRequiredMark = styled.span`
  color: ${({ theme }) => theme.colors.danger[900]};
  font-size: 6px;
  margin-left: 2px;
  position: absolute;
  top: 5px;
`;

export const ScreenReaderOnly = styled.div`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;
